/**
 * Pure H OS · Phase D · P0 · AI Insights with actionable state.
 *
 * Each insight now carries a state (open / acted / dismissed / converted)
 * persisted in the `rec_states` MongoDB collection. Insights default to
 * "open"; owners can mark them acted, dismissed, or converted-to-decision.
 */
import { useMemo, useState } from "react";
import { Card, SectionHeader, SeverityBadge, Button } from "../../components/lib";
import { TabBar, ListRow, Drawer } from "../../components/lib.overlay";
import { AI_INSIGHTS, AI_TEAMMATES, AiInsight } from "../../mocks/aiData";
import { readCollection, upsertLocal } from "../../services/store";
import { useLiveTick } from "../../lib/useLiveTick";
import { api } from "../../services/api";

type RecState = { id: string; state: "open" | "acted" | "dismissed" | "converted"; note?: string; updatedAt?: string };
type Tab = "open" | "acted" | "dismissed" | "converted" | "all";

function tname(id: string) { return AI_TEAMMATES.find((t) => t.id === id)?.name ?? id; }

function stateOf(id: string): RecState["state"] {
  const rows = readCollection<RecState>("rec_states");
  const row = rows.find((r) => r.id === id);
  return row?.state ?? "open";
}

export default function Insights() {
  const [tab, setTab] = useState<Tab>("open");
  const [openId, setOpenId] = useState<string | null>(null);
  const [busy, setBusy] = useState<RecState["state"] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useLiveTick();

  async function applyState(next: RecState["state"]) {
    if (!openId) return;
    setBusy(next); setErr(null);
    try {
      const updated = await api.patch("rec_states", openId, { state: next }) as RecState;
      upsertLocal("rec_states", { ...updated, id: openId });
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Update failed");
    } finally { setBusy(null); }
  }

  const filtered = useMemo(
    () => AI_INSIGHTS.filter((i) => tab === "all" || stateOf(i.id) === tab),
    [tab],
  );

  const current = openId ? AI_INSIGHTS.find((i) => i.id === openId) ?? null : null;

  return (
    <div className="space-y-6" data-testid="ai-insights">
      <Card testId="ai-in-filters">
        <TabBar<Tab>
          testId="ai-in-tabs"
          active={tab}
          onChange={setTab}
          tabs={[
            { key: "open",       label: "Open" },
            { key: "acted",      label: "Acted" },
            { key: "dismissed",  label: "Dismissed" },
            { key: "converted",  label: "Converted" },
            { key: "all",        label: "All" },
          ]}
        />
      </Card>

      <Card padded={false} testId="ai-in-list">
        <div className="p-5" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
          <SectionHeader eyebrow="AI Insights" title={`${filtered.length} signal${filtered.length === 1 ? "" : "s"}`} />
          <div className="text-xs" style={{ color: "var(--ph-muted)" }}>
            Every AI recommendation carries a state. Convert an insight to a Decision if it needs a governance record.
          </div>
        </div>
        <div className="p-2">
          {filtered.length === 0 ? (
            <div className="p-5 text-sm" style={{ color: "var(--ph-muted)" }}>Nothing in this bucket.</div>
          ) : filtered.map((i) => {
            const s = stateOf(i.id);
            return (
              <ListRow key={i.id} testId={`ai-in-${i.id}`}
                onClick={() => setOpenId(i.id)}
                label={
                  <span className="flex items-center gap-2">
                    <span className="uppercase tracking-[0.14em] text-[10px]" style={{ color: "var(--ph-muted)" }}>{s}</span>
                    <span>{i.title}</span>
                  </span>
                }
                sublabel={<>{tname(i.teammateId)} · {i.createdRelative} — {i.detail}</>}
                trailing={<SeverityBadge severity={i.severity === "high" ? "high" : i.severity === "medium" ? "medium" : "low"} />}
              />
            );
          })}
        </div>
      </Card>

      <Drawer open={!!current} onClose={() => setOpenId(null)} testId="ai-in-drawer" title={current ? current.title : ""}>
        {current && (
          <div className="space-y-6">
            <SectionHeader eyebrow={`${tname(current.teammateId)} · ${current.severity}`} title={`Current state: ${stateOf(current.id)}`} />
            <div className="text-sm leading-relaxed" style={{ color: "var(--ph-text)" }}>{current.detail}</div>
            <div className="pt-4" style={{ borderTop: "1px solid var(--ph-border-subtle)" }}>
              <div className="text-[10px] uppercase tracking-[0.16em] mb-2" style={{ color: "var(--ph-muted)" }}>State actions</div>
              {err && <div className="text-xs mb-2" style={{ color: "var(--ph-danger)" }} data-testid="ai-in-action-error">{err}</div>}
              <div className="flex flex-wrap gap-2">
                <Button variant="primary"   disabled={busy !== null} onClick={() => applyState("acted")}      testId="ai-in-acted">
                  {busy === "acted" ? "Marking…" : "Mark acted"}
                </Button>
                <Button variant="secondary" disabled={busy !== null} onClick={() => applyState("converted")}  testId="ai-in-convert">
                  {busy === "converted" ? "Converting…" : "Convert to Decision"}
                </Button>
                <Button variant="secondary" disabled={busy !== null} onClick={() => applyState("dismissed")}  testId="ai-in-dismiss">
                  {busy === "dismissed" ? "Dismissing…" : "Dismiss"}
                </Button>
                <Button variant="ghost"     disabled={busy !== null} onClick={() => applyState("open")}       testId="ai-in-reopen">
                  {busy === "open" ? "Reopening…" : "Reopen"}
                </Button>
              </div>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}
